import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Link2, RefreshCw, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const LoginChip = ({ p }) => {
  const s = p?.status || "none";
  const cls = s === "logged_in" ? "bg-emerald-100 text-emerald-800" : s === "expired" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600";
  const label = s === "logged_in" ? "gst.gov.in logged in" : s === "expired" ? "login expired" : "no portal login";
  return <span className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase ${cls}`}>{label}</span>;
};

export default function GstBridgePanel() {
  const [st, setSt] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/gst-bridge/status"), api.get("/gst-bridge/events")]);
    setSt(a.data);
    setEvents(b.data.items || []);
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const run = async (path, label) => {
    setBusy(path);
    try {
      const r = await api.post(`/gst-bridge/${path}`);
      const bad = (r.data.results || []).filter((x) => !x.ok);
      if (bad.length) { toast.error(`${label}: ${bad[0].error}`); } else { toast.success(`${label} done`); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(""); }
  };

  if (!st) return null;
  return (
    <Card className="p-4 rounded-sm space-y-4" data-testid="gst-bridge-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-medium flex items-center gap-2"><Link2 className="w-4 h-4" /> Zenith360-GST bridge</div>
          <p className="text-xs text-muted-foreground">GSTIN checks, filing status, GSTR-2B / IMS, notices and the gst.gov.in login come from Zenith360-GST. Changes there reflect here automatically.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5" disabled={!st.configured || !!busy} onClick={() => run("link-all", "Link companies")} data-testid="gst-bridge-link-btn">
            <Link2 className="w-3.5 h-3.5" /> Link GSTINs
          </Button>
          <Button size="sm" className="rounded-sm gap-1.5" disabled={!st.configured || !!busy} onClick={() => run("sync", "Sync")} data-testid="gst-bridge-sync-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${busy === "sync" ? "animate-spin" : ""}`} /> Sync now
          </Button>
        </div>
      </div>
      {!st.configured && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-2" data-testid="gst-bridge-not-configured">
          Bridge key not set yet. Add the shared bridge key to both Zenith360-ERP and Zenith360-GST to switch it on.
        </div>
      )}
      <div className="divide-y border rounded-sm text-sm">
        {st.gstins.map((g) => (
          <div key={g.gstin} className="flex items-center justify-between gap-2 p-2 flex-wrap" data-testid={`gst-bridge-row-${g.gstin}`}>
            <div><span className="font-mono">{g.gstin}</span> <span className="text-muted-foreground">{g.legal_name}</span></div>
            <div className="flex items-center gap-2 text-xs">
              <LoginChip p={g.portal_login} />
              <span className={`px-2 py-0.5 rounded-sm font-mono uppercase text-[10px] ${g.bridge_status === "linked" ? "bg-emerald-100 text-emerald-800" : g.bridge_status === "error" ? "bg-red-100 text-red-800" : "bg-zinc-100 text-zinc-600"}`}
                title={g.bridge_error || ""} data-testid={`gst-bridge-status-${g.gstin}`}>{g.bridge_status || "not linked"}</span>
              {g.bridge_synced_at && <span className="text-muted-foreground">synced {g.bridge_synced_at.slice(0, 16).replace("T", " ")}</span>}
            </div>
          </div>
        ))}
        {!st.gstins.length && <div className="p-3 text-xs text-muted-foreground">No company GSTINs yet.</div>}
      </div>
      <div>
        <div className="text-xs font-medium mb-1">Recent bridge activity</div>
        <div className="max-h-48 overflow-y-auto text-xs space-y-1" data-testid="gst-bridge-events">
          {events.slice(0, 20).map((e) => (
            <div key={e.id} className="flex gap-2 items-center">
              {e.direction === "in" ? <ArrowDownLeft className="w-3 h-3 text-emerald-600" /> : <ArrowUpRight className="w-3 h-3 text-sky-600" />}
              <span className="font-mono">{e.at.slice(0, 16).replace("T", " ")}</span>
              <span>{e.event}</span><span className="font-mono">{e.gstin}</span>
              <span className={e.ok ? "text-muted-foreground" : "text-red-600"}>{e.detail}</span>
            </div>
          ))}
          {!events.length && <div className="text-muted-foreground">No activity yet.</div>}
        </div>
      </div>
    </Card>
  );
}
